import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react"
import styles from "./Feed.module.scss"
import { connect } from "react-redux"
import {
  openFeedTextModal,
  fetchExploreFeed,
  feedFilterToApiView,
  resetRecentPost,
  setFeedView,
} from "../../../store/actions"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import FeedCard from "./FeedCard.tsx"
import PortfolioCard from "./PortfolioCard/PortfolioCard.tsx"
import FeedExploreFilters from "./FeedExploreFilters.tsx"
import { FileText, Package } from "lucide-react"
import FeedCardSkeleton from "./FeedCardSkeleton.tsx"
import useIsPhone from "../../../hooks/useIsPhone"
import type { FeedFilterOption, FeedItem, FeedProps } from "../../../types/feed.ts"
import {
  selectExploreFeed,
  selectFeedView,
  type FeedSelectorRootState,
} from "../../../store/feed/selectors.ts"
import { feedOptionToViewPayload } from "../../../utils/feedExploreFilterPayload.ts"

type FeedOwnProps = {
  headerLayout?: "full" | "none"
}

function getFeedColumnCount(width: number): number {
  if (width < 601) return 1
  return 2
}

function sliceItemsIntoColumns<T>(items: T[], columnCount: number): T[][] {
  if (columnCount <= 1) return [items]
  const columns: T[][] = Array.from({ length: columnCount }, () => [])
  items.forEach((item, index) => {
    columns[index % columnCount].push(item)
  })
  return columns
}

const Feed = ({
  userData,
  openFeedTextModal,
  fetchExploreFeed: dispatchFetchExploreFeed,
  resetRecentPost,
  recentPost,
  componentLoading,
  exploreFeed,
  feedView,
  setFeedView,
  headerLayout = "full",
}: FeedProps & FeedOwnProps) => {
  const selectedOption: FeedFilterOption = feedView?.selectedOption ?? "Mostrar"
  const [myPosts, setMyPosts] = useState(false)
  const [showFeedBar, setShowFeedBar] = useState(false)
  const [lastScrollPosition, setLastScrollPosition] = useState(0)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [isAppending, setIsAppending] = useState(false)

  const isPhone = useIsPhone()
  const [feedColumnCount, setFeedColumnCount] = useState(() =>
    typeof window !== "undefined" ? getFeedColumnCount(window.innerWidth) : 1
  )
  const buttonsContainerRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const loadMoreAppendRef = useRef(() => {})
  const loadMorePendingRef = useRef(false)
  const selectedOptionRef = useRef(selectedOption)
  const skipOptionFetchOnceRef = useRef(true)

  const feedItems = exploreFeed?.items ?? []
  const nextCursor = exploreFeed?.nextCursor ?? null
  const hasMore = exploreFeed?.hasMore ?? false

  const items = useMemo(
    () => (dataLoaded ? feedItems : []),
    [dataLoaded, feedItems]
  )

  useEffect(() => {
    selectedOptionRef.current = selectedOption
  }, [selectedOption])

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: isPhone ? "-50px 0px 0px 0px" : "0px",
      threshold: [0, 0.9],
    }

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries
      const currentScroll = window.scrollY
      const direction = currentScroll > lastScrollPosition ? "down" : "up"
      setLastScrollPosition(currentScroll)

      if (direction === "down") {
        setShowFeedBar(entry.intersectionRatio < 0.9)
      } else if (direction === "up") {
        setShowFeedBar(
          entry.intersectionRatio < 0.9 &&
            currentScroll > (buttonsContainerRef.current?.offsetHeight || 0)
        )
      }
    }, options)

    if (buttonsContainerRef.current) {
      observerRef.current.observe(buttonsContainerRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [lastScrollPosition, isPhone])

  useEffect(() => {
    const onResize = () => setFeedColumnCount(getFeedColumnCount(window.innerWidth))
    onResize()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    loadMoreAppendRef.current = () => {
      if (!hasMore || nextCursor == null || loadMorePendingRef.current) return
      loadMorePendingRef.current = true
      setIsAppending(true)
      ;(
        dispatchFetchExploreFeed({
          view: feedFilterToApiView(selectedOptionRef.current),
          cursor: nextCursor,
          append: true,
          silent: true,
        }) as Promise<unknown>
      )
        .catch(() => undefined)
        .finally(() => {
          loadMorePendingRef.current = false
          setIsAppending(false)
        })
    }
  }, [hasMore, nextCursor, dispatchFetchExploreFeed])

  useEffect(() => {
    if (!dataLoaded || !hasMore) return
    const el = loadMoreRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) loadMoreAppendRef.current()
        }
      },
      { root: null, rootMargin: "240px", threshold: 0 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [dataLoaded, hasMore, selectedOption, componentLoading, items.length])

  useEffect(() => {
    if (!dataLoaded || !hasMore || nextCursor == null || loadMorePendingRef.current) return
    const id = requestAnimationFrame(() => {
      if (loadMorePendingRef.current) return
      const el = loadMoreRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const vh = window.innerHeight || document.documentElement.clientHeight
      if (r.top <= vh + 280) {
        loadMoreAppendRef.current()
      }
    })
    return () => cancelAnimationFrame(id)
  }, [dataLoaded, hasMore, items.length])

  const { guard } = useActionGuard()

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setFeedView({
          selectedOption: "Mostrar",
          order: "DESC",
          type: "DATE",
        })
        await dispatchFetchExploreFeed({ view: "mostrar", append: false })
        resetRecentPost()
        setDataLoaded(true)
      } catch (error) {
        console.error("Error loading initial data:", error)
        setDataLoaded(true)
      }
    }

    if (userData?.id && !dataLoaded) {
      loadInitialData()
    }
  }, [userData?.id, dataLoaded, dispatchFetchExploreFeed, resetRecentPost, setFeedView])

  useEffect(() => {
    if (!dataLoaded) return
    if (skipOptionFetchOnceRef.current) {
      skipOptionFetchOnceRef.current = false
      return
    }

    const handleOptionChange = async () => {
      try {
        setMyPosts(selectedOption === "propios")
        await dispatchFetchExploreFeed({
          view: feedFilterToApiView(selectedOption),
          append: false,
        })
      } catch (error) {
        console.error("Error changing option:", error)
      }
    }

    handleOptionChange()
  }, [selectedOption, dataLoaded, userData?.id, dispatchFetchExploreFeed])

  useEffect(() => {
    if (recentPost?.user?.id === userData?.id) {
      setFeedView(feedOptionToViewPayload("recientes"))
    }
  }, [recentPost, userData?.id, setFeedView])

  const handleOpenMainModalTxt = () => {
    openFeedTextModal({ text: true })
  }

  const handleOptionClick = (option: FeedFilterOption) => {
    setFeedView(feedOptionToViewPayload(option))
  }

  const generateStableKey = useCallback(
    (item: FeedItem) => {
      const baseKey =
        item.itemType === "portfolio" ? `portfolio-${item.id}` : `post-${item.id}`
      return `${baseKey}-${selectedOption}`
    },
    [selectedOption]
  )

  const skeletonSlots = useMemo(() => Array.from({ length: 10 }, (_, i) => i), [])

  const renderFeedItem = useCallback(
    (item: FeedItem) => {
      const stableKey = generateStableKey(item)
      if (item.itemType === "portfolio") {
        return <PortfolioCard key={stableKey} item={item} />
      }
      return <FeedCard key={stableKey} item={item} myPosts={myPosts} />
    },
    [generateStableKey, myPosts]
  )

  const feedGridClassName =
    feedColumnCount > 1
      ? `${styles.feedItemGrid} ${styles.feedItemGrid_masonry}`
      : styles.feedItemGrid

  const showSkeletonInitial = (userData?.id && !dataLoaded) || (componentLoading && items.length > 0)
  const showEmpty =
    dataLoaded && items.length === 0 && !componentLoading

  return (
    <div
      className={`${styles.container} ${headerLayout === "none" ? styles.container_flush : ""}`}
      style={{ gridArea: "feed" }}
    >
      {headerLayout === "full" ? (
        <div className={styles.header}>
          <h2 className={styles.header_title}>Publicaciones</h2>
          <div className={styles.header_select}>
            <FeedExploreFilters selectedOption={selectedOption} onSelect={handleOptionClick} />
          </div>
        </div>
      ) : null}

      <div className={styles.feedItemContainer}>
        {showSkeletonInitial ? (
          feedColumnCount <= 1 ? (
            <div className={styles.feedItemGrid}>
              {skeletonSlots.map((index) => (
                <FeedCardSkeleton key={`skeleton-${index}`} />
              ))}
            </div>
          ) : (
            <div className={`${styles.feedItemGrid} ${styles.feedItemGrid_masonry}`}>
              {sliceItemsIntoColumns(skeletonSlots, feedColumnCount).map((col, c) => (
                <div key={c} className={styles.feedItemGrid_column}>
                  {col.map((index) => (
                    <FeedCardSkeleton key={`skeleton-${index}`} />
                  ))}
                  <div className={styles.feedItemGrid_column_fill} aria-hidden />
                </div>
              ))}
            </div>
          )
        ) : items.length > 0 && !componentLoading ? (
          <>
            {feedColumnCount <= 1 ? (
              <div className={feedGridClassName}>
                {items.map(renderFeedItem)}
                {isAppending && hasMore ? (
                  <FeedCardSkeleton key="feed-append-placeholder" />
                ) : null}
              </div>
            ) : (
              <div className={feedGridClassName}>
                {sliceItemsIntoColumns(items, feedColumnCount).map((colItems, c) => (
                  <div key={c} className={styles.feedItemGrid_column}>
                    {colItems.map(renderFeedItem)}
                    {isAppending && hasMore ? (
                      <FeedCardSkeleton key={`feed-append-placeholder-${c}`} />
                    ) : null}
                    <div className={styles.feedItemGrid_column_fill} aria-hidden />
                  </div>
                ))}
              </div>
            )}
            {hasMore ? (
              <div ref={loadMoreRef} className={styles.feedLoadMoreSentinel} aria-hidden />
            ) : (
              <p className={styles.feedEndNote} role="status">
                Llegaste al final: no hay más publicaciones para esta vista.
              </p>
            )}
          </>
        ) : showEmpty ? (
          selectedOption === "propios" ? (
            <div className={styles.feedItemContainer_empty}>
              <div className={styles.feedItemContainer_empty_icon}>
                <FileText size={34} />
              </div>
              <h2 className={styles.feedItemContainer_empty_title}>
                No tenés publicaciones propias
              </h2>
              <p className={styles.feedItemContainer_empty_text}>
                Compartí novedades, fotos y actualizaciones con la comunidad
              </p>
              <button
                className={styles.feedItemContainer_empty_button}
                onClick={guard("open_feed_modal", handleOpenMainModalTxt)}
              >
                Crear publicación
              </button>
            </div>
          ) : (
            <div className={styles.feedItemContainer_empty}>
              <div className={styles.feedItemContainer_empty_icon}>
                <Package size={34} />
              </div>
              <h2 className={styles.feedItemContainer_empty_title}>
                {selectedOption === "servicios"
                  ? "Sé el primero en publicar un servicio"
                  : selectedOption === "productos"
                    ? "Sé el primero en publicar un producto"
                    : selectedOption === "posteos"
                      ? "Sé el primero en publicar un posteo"
                      : "Sé el primero en publicar"}
              </h2>
              <p className={styles.feedItemContainer_empty_text}>
                Aún no hay publicaciones en esta categoría. Publicá el tuyo y conectá con la comunidad.
              </p>
              <button
                className={styles.feedItemContainer_empty_button}
                onClick={guard("open_feed_modal", handleOpenMainModalTxt)}
              >
                Publicar el primero
              </button>
            </div>
          )
        ) : feedColumnCount <= 1 ? (
          <div className={styles.feedItemGrid}>
            {skeletonSlots.map((index) => (
              <FeedCardSkeleton key={`skeleton-fallback-${index}`} />
            ))}
          </div>
        ) : (
          <div className={`${styles.feedItemGrid} ${styles.feedItemGrid_masonry}`}>
            {sliceItemsIntoColumns(skeletonSlots, feedColumnCount).map((col, c) => (
              <div key={c} className={styles.feedItemGrid_column}>
                {col.map((index) => (
                  <FeedCardSkeleton key={`skeleton-fallback-${index}`} />
                ))}
                <div className={styles.feedItemGrid_column_fill} aria-hidden />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: FeedSelectorRootState) => ({
  userData: state.general?.userData,
  recentPost: state.feed?.recentPost ?? null,
  componentLoading: state.general?.componentLoading,
  exploreFeed: selectExploreFeed(state),
  feedView: selectFeedView(state),
})

const ConnectedFeed = connect(mapStateToProps, {
  openFeedTextModal,
  fetchExploreFeed,
  resetRecentPost,
  setFeedView,
})(Feed)

export default ConnectedFeed as unknown as React.ComponentType<FeedOwnProps>
